import React, { useState, useEffect, useMemo } from "react";
import {
    fetchStoresForArea,
    updateStoreAreaAssignment,
} from "../../service/api/orders"; // adjust path to wherever api.js lives

function IconSearch(props) {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
    );
}

function IconCheck(props) {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );
}

function IconX(props) {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );
}

function IconMap(props) {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
            <line x1="8" y1="2" x2="8" y2="18" />
            <line x1="16" y1="6" x2="16" y2="22" />
        </svg>
    );
}

/**
 * The area is already known (passed in as a prop from the row's "Assign to
 * Stores" button). Layout matches the old blade "Assign Stores" page:
 *  - A summary card up top: "<Area> is assigned to N stores" with a pill
 *    for each currently-assigned store (read-only snapshot of saved state).
 *  - A filter box.
 *  - A pill/chip grid of every store — click any chip to toggle it in/out
 *    of the live selection; selected chips turn green, same as the blade
 *    version's checked state. Check All / Un-check All act on the whole
 *    (filtered) grid at once.
 *  - A single Update button that syncs the full set.
 *
 * onSaved(area, storeIds) fires after a successful save, before the modal closes.
 */
export default function AssignStoresToAreaModal({ open, area, onClose, onSaved }) {
    const [stores, setStores] = useState([]);
    const [assignedIds, setAssignedIds] = useState(new Set());
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [query, setQuery] = useState("");

    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    // Fresh load every time the modal opens for a given area.
    useEffect(() => {
        if (!open || !area) return;

        setQuery("");
        setSaveError(null);
        setLoading(true);
        setLoadError(null);

        let cancelled = false;

        fetchStoresForArea(area.id)
            .then((res) => {
                if (cancelled) return;
                const data = res.data || {};
                const allStores = data.restaurants || data.stores || [];
                const assigned = new Set((data.assigned_restaurant_ids || []).map(String));
                setStores(allStores);
                setAssignedIds(assigned);
                setSelectedIds(new Set(assigned));
            })
            .catch(() => {
                if (!cancelled) {
                    setLoadError("Couldn't load stores for this area.");
                    setStores([]);
                    setAssignedIds(new Set());
                    setSelectedIds(new Set());
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, area]);

    const filteredStores = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return stores;
        return stores.filter((s) => s.name.toLowerCase().includes(q));
    }, [stores, query]);

    // Currently-assigned stores, kept in their original order, for the
    // read-only summary pills at the top (snapshot of saved state).
    const assignedStores = useMemo(
        () => stores.filter((s) => assignedIds.has(String(s.id))),
        [stores, assignedIds]
    );

    const toggleStore = (id) => {
        const key = String(id);
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    // Check All / Un-check All act on whatever's currently visible (filtered).
    const checkAll = () => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            filteredStores.forEach((s) => next.add(String(s.id)));
            return next;
        });
    };

    const uncheckAll = () => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            filteredStores.forEach((s) => next.delete(String(s.id)));
            return next;
        });
    };

    const hasChanges = useMemo(() => {
        if (selectedIds.size !== assignedIds.size) return true;
        for (const id of selectedIds) {
            if (!assignedIds.has(id)) return true;
        }
        return false;
    }, [selectedIds, assignedIds]);

    const handleClose = () => {
        if (saving) return;
        onClose && onClose();
    };

    const handleSave = async () => {
        if (!area) return;
        setSaving(true);
        setSaveError(null);
        try {
            const storeIds = Array.from(selectedIds).map(Number);
            await updateStoreAreaAssignment({
                id: area.id,
                delivery_areas: storeIds,
                stores_to_area: true, // id refers to the area, delivery_areas holds store ids
            });
            onSaved && onSaved(area, storeIds);
            onClose && onClose();
        } catch (e) {
            setSaveError("Couldn't save — please try again.");
        } finally {
            setSaving(false);
        }
    };

    if (!open || !area) return null;

    return (
        <div className="asa-overlay" onMouseDown={(e) => e.target === e.currentTarget && handleClose()}>
            <div className="asa-modal asa-modal--wide">
                <div className="asa-header">
                    <div>
                        <h2>Assign Stores</h2>
                        <p className="asa-subtitle">
                            Choose which stores <strong>{area.name}</strong> should serve.
                        </p>
                    </div>
                    <button className="asa-close" onClick={handleClose} aria-label="Close">
                        ×
                    </button>
                </div>

                {/* Summary card — snapshot of what's currently saved */}
                <div className="asa-summary-card">
                    <p className="asa-summary-line">
                        <strong>{area.name}</strong> is assigned to{" "}
                        <strong>{assignedStores.length}</strong> store{assignedStores.length === 1 ? "" : "s"}.
                    </p>
                    {assignedStores.length > 0 && (
                        <div className="asa-summary-pills">
                            {assignedStores.map((s) => (
                                <span key={s.id} className="asa-summary-pill">
                                    {s.name}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* Filter */}
                <div className="asa-search-wrap asa-store-search">
                    <IconSearch className="asa-search-icon" />
                    <input
                        autoFocus
                        className="asa-search-input"
                        placeholder="Filter with store name…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                </div>

                {/* Stores section */}
                <div className="asa-toggle-section">
                    <div className="asa-toggle-header">
                        {/*<div className="asa-label-with-icon">*/}
                        {/*    <span className="asa-label-store-icon">*/}
                        {/*        <IconMap />*/}
                        {/*    </span>*/}
                        {/*    <span className="asa-label">Stores</span>*/}
                        {/*</div>*/}
                        <div className="asa-toggle-header-right">
                            <span className="asa-count">
                                {selectedIds.size} selected / {stores.length} total
                            </span>
                            <button type="button" className="asa-bulk-btn asa-bulk-btn-check" onClick={checkAll}>
                                <IconCheck /> Check All
                            </button>
                            <button type="button" className="asa-bulk-btn asa-bulk-btn-uncheck" onClick={uncheckAll}>
                                <IconX /> Un-check All
                            </button>
                        </div>
                    </div>

                    {loading && <div className="asa-state">Loading stores…</div>}
                    {loadError && <div className="asa-state asa-state-error">{loadError}</div>}
                    {!loading && !loadError && filteredStores.length === 0 && (
                        <div className="asa-state">No stores match "{query}".</div>
                    )}

                    {!loading && !loadError && filteredStores.length > 0 && (
                        <div className="asa-toggle-grid">
                            {filteredStores.map((store) => {
                                const checked = selectedIds.has(String(store.id));
                                return (
                                    <button
                                        type="button"
                                        key={store.id}
                                        className={`asa-toggle-chip${checked ? " is-checked" : ""}`}
                                        onClick={() => toggleStore(store.id)}
                                    >
                                        {store.name}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {saveError && <div className="asa-save-error">{saveError}</div>}

                <div className="asa-footer">
                    <button className="ana-btn ana-btn-ghost" onClick={handleClose} disabled={saving}>
                        Cancel
                    </button>
                    <button
                        className="ana-btn ana-btn-primary asa-update-btn"
                        onClick={handleSave}
                        disabled={!hasChanges || saving || loading}
                    >
                        {saving ? "Updating…" : "Update"}
                    </button>
                </div>
            </div>
        </div>
    );
}